/**
 * Typed wrappers over the BFF auth surface. All ceremony option/response
 * objects are opaque (`unknown`) on the wire and cast at the passkey call
 * site against `@simplewebauthn/browser`'s JSON types.
 */
import type {
  AuthMethodsResponse,
  EmailChallengeBody,
  EmailVerifyBody,
  EmailVerifyResponse,
  LoginVerifyBody,
  LogoutResponse,
  MeResponse,
  PortalContractor,
  PortalUser,
  RegisterVerifyBody,
  SignInStartBody,
  SignInStartResponse,
  SsoLookupResponse,
  SwitchContractorBody
} from '@portal/contracts'
import { apiFetch } from './portal-client'

/** Current session. The caller treats a thrown 401 as "not signed in" — no
    hard redirect here, or public routes (login, dev theater stage) bounce. */
export function getMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/api/me', undefined, { on401: 'throw' })
}

/** Move the session into another of the person's companies; answers with the new `/api/me`. */
export function switchContractor(contractorId: string): Promise<MeResponse> {
  const body: SwitchContractorBody = { contractorId }
  return apiFetch<MeResponse>('/api/me/contractor', { method: 'POST', body: JSON.stringify(body) })
}

/** Which login methods to render (passkey always; Microsoft when OIDC is enabled). */
export function getAuthMethods(): Promise<AuthMethodsResponse> {
  return apiFetch<AuthMethodsResponse>('/api/auth/methods')
}

/** Home-realm discovery: which SSO provider (if any) serves this email's domain. */
export function ssoLookup(email: string): Promise<SsoLookupResponse> {
  return apiFetch<SsoLookupResponse>(`/api/auth/sso/lookup?email=${encodeURIComponent(email)}`)
}

/** 204 → local logout done; 200 {redirectTo} → navigate there to end the IdP session too. */
export function logout(): Promise<LogoutResponse | undefined> {
  return apiFetch<LogoutResponse | undefined>('/api/auth/logout', { method: 'POST' })
}

// ── The one sign-in path ─────────────────────────────────────────────────────

/** The sign-in page's one question. The BFF decides: the company's SSO for its domain, else a code (same answer for every address). */
export function signInStart(body: SignInStartBody): Promise<SignInStartResponse> {
  return apiFetch('/api/auth/start', { method: 'POST', body: JSON.stringify(body) })
}

/** Where the company SSO starts — a full-page navigation (the BFF sets the transaction cookie and 302s to the IdP). */
export function ssoStartUrl(slug: string, returnTo: string, loginHint?: string): string {
  const qs = new URLSearchParams({ provider: slug, returnTo })
  if (loginHint) qs.set('login_hint', loginHint)
  return `/auth/oidc/start?${qs.toString()}`
}

// ── Email bootstrap (register / add-device) ──────────────────────────────────

/** Always resolves 202 {status:'sent', tokenId} — never reveals account existence. */
export function emailChallenge(body: EmailChallengeBody): Promise<{ status: 'sent'; tokenId: string }> {
  return apiFetch('/api/auth/email-challenge', { method: 'POST', body: JSON.stringify(body) })
}

/**
 * Verifies the emailed code. Purpose register/add-device → a short-lived bootstrap cookie (go on to the passkey);
 * purpose login → a session (signed in with the code).
 */
export function emailVerify(body: EmailVerifyBody): Promise<EmailVerifyResponse> {
  return apiFetch('/api/auth/email-verify', { method: 'POST', body: JSON.stringify(body) })
}

// ── Passkey registration ─────────────────────────────────────────────────────

/** Returns PublicKeyCredentialCreationOptionsJSON (opaque to the SPA). */
export function registerOptions(body: { label?: string }): Promise<unknown> {
  return apiFetch('/api/auth/register-options', { method: 'POST', body: JSON.stringify(body) })
}

/** Persists the new credential + opens a session. */
export function registerVerify(body: RegisterVerifyBody): Promise<{ credentialId: string }> {
  return apiFetch('/api/auth/register-verify', { method: 'POST', body: JSON.stringify(body) })
}

// ── Passkey login ────────────────────────────────────────────────────────────

/** Returns PublicKeyCredentialRequestOptionsJSON (opaque to the SPA). */
export function loginOptions(): Promise<unknown> {
  return apiFetch('/api/auth/login-options', { method: 'POST', body: JSON.stringify({}) })
}

/** Verifies the assertion + opens a session. */
export function loginVerify(body: LoginVerifyBody): Promise<{ user: PortalUser; contractor: PortalContractor }> {
  return apiFetch('/api/auth/login-verify', { method: 'POST', body: JSON.stringify(body) })
}
